import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrashOutlined";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import PeopleIcon from "@mui/icons-material/PeopleOutline";
import NavbarLoggedInNew from "components/Navbar-new/NavbarLoggedInNew";
import ModuleCard from "./ModuleCard";
import ChapterUploadDrawer from "./ChapterUploadDrawer";
import useDragReorder from "./useDragReorder";
import * as bankApi from "./contentBankApi";
import {
  ConfirmDialog,
  CopyChaptersDialog,
  PracticeDialog,
  TextPromptDialog,
  TrashDialog,
  VersionsDialog,
} from "./dialogs";
import { plural } from "./format";

const FONT = "Be Vietnam Pro, sans-serif";

const STATUS_COLORS = {
  active: { bg: "#DCFCE7", fg: "#15803D" },
  paused: { bg: "#FEF3C7", fg: "#B45309" },
  completed: { bg: "#DBEAFE", fg: "#1D4ED8" },
  archived: { bg: "#F1F5F9", fg: "#64748B" },
};

const StatusChip = ({ status, label }) => {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.archived;
  return (
    <Chip size="small" label={label || status}
      sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: colors.bg, color: colors.fg }} />
  );
};

const sourceKey = (item) => (item.type === "batch" ? `batch-${item.batch_id}` : `course-${item.course_id}`);

/**
 * The content bank: pick 1:1 or Cohort, pick the course (or batch), then see
 * and edit every module and chapter. Changes go live for students at once.
 */
const ContentBankPage = () => {
  const [params, setParams] = useSearchParams();
  const type = params.get("type") === "cohort" ? "cohort" : "1:1";

  const [sources, setSources] = useState(null);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [highlight, setHighlight] = useState(null);
  const [toast, setToast] = useState(null); // { message, undo? }

  // Dialogs
  const [upload, setUpload] = useState(null); // { module, chapter? }
  const [prompt, setPrompt] = useState(null); // { kind: "newModule"|"renameModule"|"renameChapter", item? }
  const [confirm, setConfirm] = useState(null); // { module }
  const [versionsFor, setVersionsFor] = useState(null);
  const [copyInto, setCopyInto] = useState(null);
  const [practiceFor, setPracticeFor] = useState(null);
  const [moveChapter, setMoveChapter] = useState(null);
  const [trashOpen, setTrashOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // ─── Sources (1:1 courses or cohort batches) ──────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setSources(null);
    setError("");
    bankApi.getSources(type)
      .then((data) => { if (!cancelled) setSources(data.items); })
      .catch((err) => { if (!cancelled) setError(bankApi.errorMessage(err, "Couldn't load the list")); });
    return () => { cancelled = true; };
  }, [type]);

  const selected = useMemo(() => {
    if (!sources?.length) return null;
    const batchId = Number(params.get("batch"));
    const courseId = Number(params.get("course"));
    return (
      (type === "cohort" && sources.find((s) => s.batch_id === batchId)) ||
      (type === "1:1" && sources.find((s) => s.course_id === courseId)) ||
      sources[0]
    );
  }, [sources, params, type]);

  const courseId = selected?.course_id;

  const loadCourse = useCallback(async ({ quiet } = {}) => {
    if (!courseId) return;
    if (!quiet) setLoading(true);
    try {
      setCourse(await bankApi.getCourse(courseId));
      setError("");
    } catch (err) {
      setError(bankApi.errorMessage(err, "Couldn't load the course"));
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    setCourse(null);
    setCollapsed(new Set());
    setQuery("");
    loadCourse();
  }, [loadCourse]);

  const pickType = (next) => {
    if (next && next !== type) setParams({ type: next });
  };

  const pickSource = (key) => {
    const item = sources.find((s) => sourceKey(s) === key);
    if (!item) return;
    setParams(item.type === "batch" ? { type: "cohort", batch: String(item.batch_id) } : { type: "1:1", course: String(item.course_id) });
  };

  // ─── Feedback helpers ─────────────────────────────────────────────────────
  const notify = (message, undo) => setToast({ message, undo, key: Date.now() });
  const fail = (err, fallback) => notify(bankApi.errorMessage(err, fallback));
  const flash = (moduleId) => {
    setHighlight(moduleId);
    setTimeout(() => setHighlight((current) => (current === moduleId ? null : current)), 2500);
  };

  const students = course?.students ?? selected?.students ?? 0;
  const audience = type === "cohort"
    ? `The ${plural(students, "student")} in this batch`
    : `All ${plural(students, "student")} on this course`;

  // ─── Modules ──────────────────────────────────────────────────────────────
  const modules = useMemo(() => course?.modules || [], [course]);
  const { order: orderedModules, handleProps: moduleHandle, rowProps: moduleRow, move: shiftModule } = useDragReorder(
    modules,
    async (ids) => {
      try {
        await bankApi.reorderModules(courseId, ids);
        await loadCourse({ quiet: true });
      } catch (err) {
        fail(err, "Couldn't reorder the modules");
        loadCourse({ quiet: true });
      }
    },
  );

  const searching = query.trim().length > 0;
  const visibleModules = useMemo(() => {
    if (!searching) return orderedModules;
    const q = query.trim().toLowerCase();
    return orderedModules
      .map((m) => {
        if (m.name.toLowerCase().includes(q)) return m;
        const chapters = m.chapters.filter((c) =>
          c.title.toLowerCase().includes(q) || (c.html_filename || "").toLowerCase().includes(q));
        return chapters.length ? { ...m, chapters } : null;
      })
      .filter(Boolean);
  }, [orderedModules, query, searching]);

  const stats = useMemo(() => ({
    modules: modules.length,
    chapters: modules.reduce((n, m) => n + m.chapters.length, 0),
    empty: modules.filter((m) => m.chapters.length === 0).length,
    deleted: (course?.deleted_modules?.length || 0) + modules.reduce((n, m) => n + m.deleted_chapters.length, 0),
  }), [modules, course]);

  const submitPrompt = async (value) => {
    setBusy(true);
    try {
      if (prompt.kind === "newModule") {
        const created = await bankApi.createModule(courseId, value);
        await loadCourse({ quiet: true });
        flash(created.id);
        notify(`Module “${value}” added`);
      } else if (prompt.kind === "renameModule") {
        await bankApi.updateModule(prompt.item.id, { name: value });
        await loadCourse({ quiet: true });
        notify("Module renamed");
      } else if (prompt.kind === "renameChapter") {
        await bankApi.updateChapter(prompt.item.id, { title: value });
        await loadCourse({ quiet: true });
        notify("Chapter renamed");
      }
      setPrompt(null);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const deleteModule = async () => {
    const module = confirm.module;
    setBusy(true);
    try {
      await bankApi.deleteModule(module.id);
      setConfirm(null);
      await loadCourse({ quiet: true });
      notify(`Module “${module.name}” deleted`, async () => {
        await bankApi.restoreModule(module.id);
        await loadCourse({ quiet: true });
        notify("Module restored");
      });
    } catch (err) {
      fail(err, "Couldn't delete the module");
    } finally {
      setBusy(false);
    }
  };

  const moduleActions = {
    rename: (module) => setPrompt({ kind: "renameModule", item: module }),
    remove: (module) => setConfirm({ module }),
    copyFrom: (module) => setCopyInto(module),
    practice: (module) => setPracticeFor(module),
    shift: (module, delta) => shiftModule(module.id, delta),
  };

  // ─── Chapters ─────────────────────────────────────────────────────────────
  const chapterActions = {
    add: (module) => setUpload({ module }),
    replace: (chapter) => {
      const module = modules.find((m) => m.chapters.some((c) => c.id === chapter.id));
      setUpload({ module, chapter });
    },
    preview: async (chapter) => {
      const tab = window.open("about:blank", "_blank");
      try {
        const url = await bankApi.getPreviewUrl(chapter.id);
        if (tab) tab.location.href = url;
        else window.open(url, "_blank");
      } catch (err) {
        if (tab) tab.close();
        fail(err, "Couldn't open the preview");
      }
    },
    download: async (chapter, as) => {
      notify(as === "md" ? "Preparing Markdown…" : "Preparing the HTML package…");
      try {
        await bankApi.downloadChapter(chapter.id, as);
      } catch (err) {
        fail(err, "Download failed");
      }
    },
    rename: (chapter) => setPrompt({ kind: "renameChapter", item: chapter }),
    toggleHidden: async (chapter) => {
      try {
        await bankApi.updateChapter(chapter.id, { hidden: !chapter.hidden });
        await loadCourse({ quiet: true });
        notify(chapter.hidden ? "Chapter visible to students" : "Chapter hidden from students");
      } catch (err) {
        fail(err);
      }
    },
    versions: (chapter) => setVersionsFor(chapter),
    move: (chapter) => setMoveChapter(chapter),
    remove: async (chapter) => {
      try {
        await bankApi.deleteChapter(chapter.id);
        await loadCourse({ quiet: true });
        notify(`“${chapter.title}” deleted`, async () => {
          await bankApi.restoreChapter(chapter.id);
          await loadCourse({ quiet: true });
          notify("Chapter restored");
        });
      } catch (err) {
        fail(err, "Couldn't delete the chapter");
      }
    },
    reorder: async (module, ids) => {
      try {
        await bankApi.reorderChapters(module.id, ids);
        await loadCourse({ quiet: true });
      } catch (err) {
        fail(err, "Couldn't reorder the chapters");
        loadCourse({ quiet: true });
      }
    },
  };

  const toggleModule = (id) => setCollapsed((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allCollapsed = modules.length > 0 && modules.every((m) => collapsed.has(m.id));

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F8FAFC", fontFamily: FONT }}>
      <NavbarLoggedInNew />
      <Box sx={{ maxWidth: 1080, mx: "auto", px: { xs: 2, md: 4 }, py: { xs: 3, md: 4 } }}>
        <Typography sx={{ fontFamily: FONT, fontSize: { xs: 24, md: 28 }, fontWeight: 700, color: "#0F172A" }}>
          Content Bank
        </Typography>
        <Typography sx={{ color: "#64748B", mt: 0.5 }}>
          Every module and chapter your students read. Changes go live immediately.
        </Typography>

        {/* Picker */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mt: 3 }}>
          <ToggleButtonGroup exclusive size="small" value={type} onChange={(_, v) => pickType(v)}
            sx={{ bgcolor: "#fff", "& .MuiToggleButton-root": { px: 2.5, fontWeight: 600, textTransform: "none" },
              "& .Mui-selected": { bgcolor: "#0172A6 !important", color: "#fff !important" } }}>
            <ToggleButton value="1:1">1:1</ToggleButton>
            <ToggleButton value="cohort">Cohort</ToggleButton>
          </ToggleButtonGroup>

          <FormControl size="small" sx={{ minWidth: 300, flex: { xs: "1 1 100%", sm: "0 1 420px" }, bgcolor: "#fff" }}
            disabled={!sources?.length}>
            <InputLabel>{type === "cohort" ? "Batch" : "Course"}</InputLabel>
            <Select label={type === "cohort" ? "Batch" : "Course"} value={selected ? sourceKey(selected) : ""}
              onChange={(e) => pickSource(e.target.value)}
              renderValue={() => selected && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{selected.name}</Box>
                  {selected.status && <StatusChip status={selected.status} label={selected.status_label} />}
                </Box>
              )}>
              {(sources || []).map((s) => (
                <MenuItem key={sourceKey(s)} value={sourceKey(s)}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 500 }} noWrap>{s.name}</Typography>
                      <Typography sx={{ fontSize: 12, color: "#64748B" }} noWrap>
                        {plural(s.students, "student")}{s.course_name ? ` · ${s.course_name}` : ""}
                      </Typography>
                    </Box>
                    {s.status && <StatusChip status={s.status} label={s.status_label} />}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ flex: 1 }} />
          {course && (
            <>
              <Button size="small" startIcon={allCollapsed ? <UnfoldMoreIcon /> : <UnfoldLessIcon />}
                onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(modules.map((m) => m.id)))}>
                {allCollapsed ? "Expand all" : "Collapse all"}
              </Button>
              <Button size="small" startIcon={<RestoreFromTrashIcon />} onClick={() => setTrashOpen(true)}>
                Recently deleted{stats.deleted ? ` (${stats.deleted})` : ""}
              </Button>
            </>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        {sources && sources.length === 0 && (
          <Alert severity="info" sx={{ mt: 3 }}>
            {type === "cohort" ? "You have no cohort batches yet." : "There are no 1:1 courses yet."}
          </Alert>
        )}

        {/* Summary + search */}
        {course && (
          <Box sx={{ mt: 2.5, mb: 2, p: 1.5, pl: 2, bgcolor: "#fff", border: "1px solid #E2E8F0", borderRadius: 2,
            display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#0172A6" }}>
              <PeopleIcon fontSize="small" />
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Changes apply to {audience.charAt(0).toLowerCase() + audience.slice(1)}</Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: "#64748B" }}>
              {plural(stats.modules, "module")} · {plural(stats.chapters, "chapter")}
              {stats.empty ? ` · ${stats.empty} without chapters` : ""}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <TextField size="small" placeholder="Search modules and chapters" value={query}
              onChange={(e) => setQuery(e.target.value)} sx={{ width: { xs: "100%", sm: 280 } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          </Box>
        )}

        {loading && <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress /></Box>}

        {course && !loading && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {searching && (
              <Typography sx={{ fontSize: 13, color: "#64748B" }}>
                {visibleModules.length ? "Showing matches. Clear the search to reorder." : "Nothing matches your search."}
              </Typography>
            )}
            {visibleModules.map((module, index) => (
              <ModuleCard
                key={module.id}
                module={module}
                index={searching ? modules.findIndex((m) => m.id === module.id) : index}
                total={modules.length}
                expanded={searching || !collapsed.has(module.id)}
                onToggle={() => toggleModule(module.id)}
                handleProps={searching ? {} : moduleHandle(module.id)}
                rowProps={searching ? {} : moduleRow(module.id)}
                reorderable={!searching}
                highlight={highlight === module.id}
                moduleActions={moduleActions}
                chapterActions={chapterActions}
              />
            ))}
            {!searching && (
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setPrompt({ kind: "newModule" })}
                sx={{ alignSelf: "flex-start", mt: 1, bgcolor: "#fff", textTransform: "none", fontWeight: 600 }}>
                Add module
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* ─── Dialogs ─── */}
      <ChapterUploadDrawer
        open={!!upload}
        module={upload?.module}
        chapter={upload?.chapter}
        audience={audience}
        onClose={() => setUpload(null)}
        onPublished={() => {
          const moduleId = upload?.module?.id;
          loadCourse({ quiet: true }).then(() => moduleId && flash(moduleId));
        }}
      />

      <TextPromptDialog
        open={!!prompt}
        busy={busy}
        title={{ newModule: "Add a module", renameModule: "Rename module", renameChapter: "Rename chapter" }[prompt?.kind]}
        label={prompt?.kind === "renameChapter" ? "Chapter title" : "Module name"}
        initial={prompt?.kind === "renameModule" ? prompt.item.name : prompt?.kind === "renameChapter" ? prompt.item.title : ""}
        confirmLabel={prompt?.kind === "newModule" ? "Add module" : "Save"}
        note={prompt?.kind === "newModule"
          ? `It's added at the end for ${audience.charAt(0).toLowerCase() + audience.slice(1)}, closed until you open it for them.`
          : prompt?.kind === "renameModule" ? `${audience} see the new name.` : null}
        onSubmit={submitPrompt}
        onClose={() => setPrompt(null)}
      />

      <ConfirmDialog
        open={!!confirm}
        busy={busy}
        title={`Delete “${confirm?.module.name}”?`}
        body={`${audience} will no longer see this module or its ${plural(confirm?.module.chapters.length || 0, "chapter")}. Their progress is kept, and you can restore it from Recently deleted.`}
        confirmLabel="Delete module"
        onConfirm={deleteModule}
        onClose={() => setConfirm(null)}
      />

      <VersionsDialog
        chapter={versionsFor}
        onClose={() => setVersionsFor(null)}
        onRestored={async () => {
          setVersionsFor(null);
          await loadCourse({ quiet: true });
          notify("Version restored");
        }}
      />

      <CopyChaptersDialog
        target={copyInto}
        onClose={() => setCopyInto(null)}
        onCopied={async (count) => {
          const moduleId = copyInto.id;
          setCopyInto(null);
          await loadCourse({ quiet: true });
          flash(moduleId);
          notify(`${plural(count, "chapter")} copied`);
        }}
      />

      <PracticeDialog
        module={practiceFor}
        sourceName={selected?.name}
        students={students}
        isCohort={type === "cohort"}
        onClose={() => setPracticeFor(null)}
      />

      <MoveChapterDialog
        chapter={moveChapter}
        modules={modules}
        onClose={() => setMoveChapter(null)}
        onMoved={async (target) => {
          setMoveChapter(null);
          await loadCourse({ quiet: true });
          flash(target.id);
          notify(`Moved to “${target.name}”`);
        }}
      />

      <TrashDialog open={trashOpen} course={course} onClose={() => setTrashOpen(false)}
        onChanged={() => loadCourse({ quiet: true })} />

      <Snackbar
        key={toast?.key}
        open={!!toast}
        autoHideDuration={toast?.undo ? 8000 : 4000}
        onClose={(_, reason) => reason !== "clickaway" && setToast(null)}
        message={toast?.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        action={toast?.undo && (
          <Button color="inherit" size="small" sx={{ fontWeight: 700 }}
            onClick={async () => {
              const undo = toast.undo;
              setToast(null);
              try { await undo(); } catch (err) { fail(err, "Couldn't undo"); }
            }}>
            Undo
          </Button>
        )}
      />
    </Box>
  );
};

const MoveChapterDialog = ({ chapter, modules, onClose, onMoved }) => {
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const current = modules.find((m) => m.chapters.some((c) => c.id === chapter?.id));

  useEffect(() => { setTarget(""); setError(""); }, [chapter]);

  const move = async () => {
    setBusy(true);
    try {
      await bankApi.moveChapter(chapter.id, target);
      onMoved(modules.find((m) => m.id === target));
    } catch (err) {
      setError(bankApi.errorMessage(err, "Couldn't move the chapter"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!chapter} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Move chapter
        <Typography sx={{ fontSize: 13, color: "#6B7280" }}>{chapter?.title}</Typography>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
          <InputLabel>Move to</InputLabel>
          <Select label="Move to" value={target} onChange={(e) => setTarget(e.target.value)}>
            {modules.map((m) => (
              <MenuItem key={m.id} value={m.id} disabled={m.id === current?.id}>{m.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography sx={{ fontSize: 13, color: "#6B7280", mt: 1.5 }}>It's added at the end of that module.</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={move} disabled={busy || !target}>Move</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContentBankPage;
